import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import GooglePayButton from '@google-pay/button-react';
import classNames from 'classnames';
import { FieldComponentProps, IPayError, IPaySuccess } from '../../../types';
import { useDataContext } from '../../../context/dataContainer';
import { clamp, DEFAULT_GOOGLE_PAY_BUTTON_TYPE, googlePayBtnLimits, isValidGooglePayButtonType } from './util';
import { getButtonColorUtil } from '../../../utils';
import { decryptGooglePayData } from '../../../api';
import { DecryptStatus } from '../../../constants';
import { TGooglePayAllowedCardNetworks } from './types';

export const GooglePay: React.FC<FieldComponentProps> = (props: FieldComponentProps) => {
  const [search] = useSearchParams();
  const buttonType = search.get('buttonType');
  const buttonColor = search.get('buttonColor') as React.ComponentProps<typeof GooglePayButton>['buttonColor'];
  const buttonLocale = search.get('buttonLocale');
  const buttonRadius = search.get('buttonRadius');
  const width = search.get('width');
  const height = search.get('height');
  const includeBIN = search.get('includeBIN') === 'true';

  const { currentLanguage, toggleFetching, isFetching, sendGooglePayTokenToLib, notifyGooglePayBtnClicked } = props;
  const {
    amount,
    currencyCode,
    paymentMethod,
    clientId,
    style,
    encryption,
  } = useDataContext();

  const environment = import.meta.env.VITE_GOOGLE_PAY_ENVIRONMENT as
    | 'PRODUCTION'
    | 'TEST';

  const merchantId = import.meta.env.VITE_GOOGLE_PAY_MERCHANT_ID as string;

  const allowedCardNetworks = useMemo(
    () =>
      (paymentMethod.credit
        ? paymentMethod.credit.map((el) => el.toUpperCase())
        : ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER', 'JCB']) as TGooglePayAllowedCardNetworks,
    [paymentMethod],
  );

  const getISO3166CodeFromCurrency = (currencyCode: string | undefined): {
    iso3166Code: string;
    iso4217CurrencyCode: string;
  } => {
    switch (currencyCode) {
      case '124':
        return { iso3166Code: 'CA', iso4217CurrencyCode: 'CAD' };
      case '840':
        return { iso3166Code: 'US', iso4217CurrencyCode: 'USD' };
      case '978':
        return { iso3166Code: 'DE', iso4217CurrencyCode: 'EUR' };
      case '826':
        return { iso3166Code: 'GB', iso4217CurrencyCode: 'GBP' };
      case '392':
        return { iso3166Code: 'JP', iso4217CurrencyCode: 'JPY' };
      case '156':
        return { iso3166Code: 'CN', iso4217CurrencyCode: 'CNY' };
      default:
        return { iso3166Code: 'US', iso4217CurrencyCode: 'USD' };
    }
  };

  const { iso3166Code, iso4217CurrencyCode } = useMemo(() => getISO3166CodeFromCurrency(currencyCode), [currencyCode]);

  const handlePayment = async (paymentData) => {
    try {
      if (!encryption || !encryption.key || !encryption.keyId) {
        sendGooglePayTokenToLib({ status: DecryptStatus.FAILED, message: 'Public key is missing' });
        return;
      }

      const response = await decryptGooglePayData({
        googlePaymentData: paymentData.paymentMethodData,
        publicKey: encryption.key,
        keyId: encryption.keyId,
        includeBIN,
      });

      if ((response as IPayError).title) {
        const { title } = response as IPayError;
        sendGooglePayTokenToLib({ status: DecryptStatus.FAILED, message: title });
        return;
      }

      sendGooglePayTokenToLib({ status: DecryptStatus.SUCCESS, data: { ...response as IPaySuccess } });
    } catch (e) {
      sendGooglePayTokenToLib({ status: DecryptStatus.FAILED, message: (e as Error).message });
    }
  };

  return (
    <div>
      <GooglePayButton
        environment={environment || 'TEST'}
        buttonColor={buttonColor || getButtonColorUtil(style?.bodyBg)}
        buttonType={isValidGooglePayButtonType(buttonType) 
          ? buttonType 
          : isValidGooglePayButtonType(style?.googlePayButtonType) 
            ? style?.googlePayButtonType 
            : DEFAULT_GOOGLE_PAY_BUTTON_TYPE}
        buttonSizeMode='fill'
        buttonLocale={buttonLocale || currentLanguage || 'en'}
        buttonRadius={clamp(buttonRadius, googlePayBtnLimits.borderRadius)}
        className={classNames('google-pay-button', { 'disabled': isFetching })}
        style={{
          width: clamp(width, googlePayBtnLimits.width),
          height: clamp(height, googlePayBtnLimits.height),
        }}
        paymentRequest={{
          apiVersion: 2,
          emailRequired: true,
          shippingAddressRequired: false,
          apiVersionMinor: 0,
          allowedPaymentMethods: [
            {
              type: 'CARD',
              parameters: {
                allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                allowedCardNetworks,
                billingAddressRequired: true,
                billingAddressParameters: {
                  format: 'FULL',
                },
              },
              tokenizationSpecification: {
                type: 'PAYMENT_GATEWAY',
                parameters: {
                  gateway: 'trxservices',
                  gatewayMerchantId: 'googletest',
                },
              },
            },
          ],
          merchantInfo: {
            merchantId: merchantId,
            merchantName: `${clientId}`,
          },
          transactionInfo: {
            totalPriceStatus: 'FINAL',
            totalPriceLabel: 'Total',
            totalPrice: String(amount),
            currencyCode: iso4217CurrencyCode,
            countryCode: iso3166Code,
          },
        }}
        onLoadPaymentData={handlePayment}
        onClick={(e) => {
          if (isFetching) {
            e.preventDefault();
          }

          notifyGooglePayBtnClicked?.();
          toggleFetching(true);
        }}
        onCancel={() => {
          toggleFetching(false);
          sendGooglePayTokenToLib({ status: DecryptStatus.CANCELLED });
        }}
        onError={(error: Error | google.payments.api.PaymentsError) => {
          toggleFetching(false);
          sendGooglePayTokenToLib({ status: DecryptStatus.FAILED, message: (error as Error).message });
        }}
      />
    </div>
  );
};
